import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Tooltip from '@mui/material/Tooltip';
import { enqueueSnackbar } from 'notistack';
import { saveAs } from 'file-saver';
import { useHistory, useParams } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';

import {
  cancelOrphanDeletion,
  deleteOrphanFiles,
  exportS3StorageAudit,
  getOrphanDeletions,
  getS3StorageAudit,
  getS3StorageAuditAsset,
  getS3StorageAuditSummary,
  OrphanDeletion,
  restoreOrphanDeletion,
  S3StorageAuditAsset,
  S3StorageAuditAssetDetails,
  S3StorageAuditBreakdownItem,
  S3StorageAuditFilters,
  S3StorageAuditResponse,
  S3StorageAuditVideofeedGroup,
  startOrphanFileScan,
  startS3StorageAudit,
} from '../../../api/s3StorageAuditApi';
import { getOrganizationRequest } from '../../../api/organizationsApi';
import { ORGANIZATION } from '../../../enums/TYPES';
import { PATHS } from '../../../enums/PATHS';
import useDebounce from '../../../helpers/useDebounce';
import { useAppSelector } from '../../../store/hooks';
import { ContainedButton, OutlinedButton } from '../../modules/Buttons';
import LinearProgress from '../../modules/LinearProgress';
import { PaginationFooter } from '../../modules/PaginationFooter';
import { formatStorageBytes, formatStorageDate, formatStorageLabel } from './utils';
import styles from './S3StorageDetails.module.scss';

const CLASSIFICATIONS = ['TRACKED', 'UNLINKED_VIDEO', 'NO_DB_RECORD', 'ARCHIVED_BUT_PRESENT', 'SHARED', 'UNKNOWN'];
const FILE_TYPES = ['SOURCE', 'HLS', 'THUMBNAIL', 'METADATA', 'OTHER'];
const ORPHAN_SCAN_POLICY_VERSION = 3;
const DB_STATUSES = [
  'CREATED',
  'UPLOADING',
  'in_progress',
  'uploaded',
  'SUBMITTED',
  'QUEUED',
  'TRANSCODING',
  'TRANSCODED',
  'TRANSCODING_FAILED',
  'transcoded',
  'failed',
  'DONE',
  'ARCHIVED',
];
const ACTIVE_SCAN_STATUSES = ['REQUESTED', 'PENDING', 'QUEUED', 'SCANNING', 'RUNNING'];
const orphanDeletionIsActive = (deletion: OrphanDeletion) => {
  if (deletion.status === 'PENDING') return !deletion.can_restore;
  if (deletion.status !== 'PROCESSING') return false;
  return Number(deletion.object_count || 0) > 0;
};

type RouteParams = { organizationId: string };

const SummaryCard = ({ label, value, detail }: { label: string; value: string; detail?: string }) => (
  <div className={styles.summaryCard}>
    <span>{label}</span>
    <strong>{value}</strong>
    {detail && <small>{detail}</small>}
  </div>
);

const Breakdown = ({ title, items }: { title: string; items: S3StorageAuditBreakdownItem[] }) => (
  <section className={styles.breakdownCard}>
    <h3>{title}</h3>
    {items.length ? (
      items.map((item) => (
        <div className={styles.breakdownRow} key={item.key}>
          <span>{item.label || formatStorageLabel(item.key)}</span>
          <span>{item.count.toLocaleString()}</span>
          <strong>{formatStorageBytes(item.size_bytes)}</strong>
        </div>
      ))
    ) : (
      <div className={styles.emptyBreakdown}>No data</div>
    )}
  </section>
);

const OrphanFileBadge = () => <span className={styles.orphanFileBadge}>Orphan file</span>;

const StorageScopeBadge = ({ scope }: { scope: S3StorageAuditAsset['storage_scope'] }) => {
  if (!scope) return null;
  const legacy = scope !== 'CURRENT_SERVER';
  return (
    <Tooltip arrow placement="top" title={legacy ? 'Stored in another shared-bucket or legacy path.' : 'Stored under this server prefix.'}>
      <span className={`${styles.storageScopeBadge} ${legacy ? styles.legacyScope : ''}`}>
        {legacy ? 'Shared / legacy path' : 'Current server'}
      </span>
    </Tooltip>
  );
};

const LastModified = ({ value }: { value?: string | null }) => {
  const elapsed = value ? Date.now() - new Date(value).getTime() : NaN;
  const days = Number.isFinite(elapsed) ? Math.max(0, Math.floor(elapsed / 86_400_000)) : null;
  return (
    <span title={days === null ? undefined : `${days.toLocaleString()} day${days === 1 ? '' : 's'} ago`}>{formatStorageDate(value)}</span>
  );
};

const orphanDeletionProgress = (deletion: OrphanDeletion) => {
  const total = Math.max(0, Number(deletion.object_count || 0));
  const completed = Math.min(
    total,
    Math.max(0, Number(deletion.processed_object_count ?? (deletion.status === 'ACTIVE' ? total : 0))),
  );
  const calculated = total ? (completed / total) * 100 : 0;
  const percent = Number.isFinite(Number(deletion.progress_percent)) ? Number(deletion.progress_percent) : calculated;
  return { completed, percent: Math.min(100, Math.max(0, percent)) };
};

const S3StorageDetails = () => {
  const { organizationId } = useParams<RouteParams>();
  const history = useHistory();
  const sharedSocket = useAppSelector((state) => state.sharedStore.socket);
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const [localSocket, setLocalSocket] = useState<Socket | null>(null);
  const [organization, setOrganization] = useState<ORGANIZATION | null>(null);
  const [audit, setAudit] = useState<S3StorageAuditResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanLoading, setScanLoading] = useState(false);
  const [orphanScanLoading, setOrphanScanLoading] = useState(false);
  const [orphanDeletions, setOrphanDeletions] = useState<OrphanDeletion[]>([]);
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [cancelLoadingId, setCancelLoadingId] = useState<number | null>(null);
  const [restoreLoadingId, setRestoreLoadingId] = useState<number | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [classification, setClassification] = useState('');
  const [fileType, setFileType] = useState('');
  const [dbStatus, setDbStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minSizeGb, setMinSizeGb] = useState('');
  const [sort, setSort] = useState('size_desc');
  const [orphansOnly, setOrphansOnly] = useState(false);
  const [viewMode, setViewMode] = useState<'videofeeds' | 'videos'>('videofeeds');
  const [expandedVideofeedId, setExpandedVideofeedId] = useState<string | null>(null);
  const [expandedAssetId, setExpandedAssetId] = useState<number | null>(null);
  const [assetDetails, setAssetDetails] = useState<Record<number, S3StorageAuditAssetDetails>>({});
  const [assetLoadingId, setAssetLoadingId] = useState<number | null>(null);
  const auditRequestRef = useRef(0);
  const summaryRequestRef = useRef(0);
  const deletionRequestRef = useRef(0);
  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;
  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    if (sharedSocket || !currentUser?.id) return;
    const created = io(`${process.env.REACT_APP_SOCKET_URL}`, {
      query: { userId: currentUser.id },
    });
    setLocalSocket(created);
    return () => {
      created.disconnect();
      setLocalSocket(null);
    };
  }, [currentUser?.id, sharedSocket]);
  const socket = sharedSocket || localSocket;

  const requestFilters = useMemo<S3StorageAuditFilters>(
    () => ({
      page: page + 1,
      limit,
      ...(sort ? { sort } : {}),
      ...(orphansOnly
        ? { orphansOnly: true }
        : {
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(classification ? { classification } : {}),
            ...(fileType ? { fileType } : {}),
            ...(dbStatus ? { dbStatus } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            ...(minSizeGb && Number(minSizeGb) >= 0 ? { minSize: Number(minSizeGb) * 1_000_000_000 } : {}),
            ...(viewMode === 'videofeeds' ? { groupBy: 'videofeed' as const } : {}),
          }),
    }),
    [page, limit, debouncedSearch, classification, fileType, dbStatus, dateFrom, dateTo, minSizeGb, sort, viewMode, orphansOnly],
  );

  const fetchAudit = useCallback(
    async (silent = false) => {
      const requestId = ++auditRequestRef.current;
      if (!silent) setLoading(true);
      setError('');
      try {
        const response = await getS3StorageAudit(organizationId, requestFilters);
        if (requestId === auditRequestRef.current) setAudit(response.data);
        return true;
      } catch (requestError: any) {
        if (requestId === auditRequestRef.current) {
          setError(requestError?.response?.data?.message || 'Failed to load S3 storage data.');
        }
        return false;
      } finally {
        if (requestId === auditRequestRef.current) setLoading(false);
      }
    },
    [organizationId, requestFilters],
  );

  const loadOrphanDeletions = useCallback(async (silent = false) => {
    const requestId = ++deletionRequestRef.current;
    try {
      const response = await getOrphanDeletions(organizationId);
      if (requestId === deletionRequestRef.current && organizationIdRef.current === organizationId) {
        setOrphanDeletions(response.data);
      }
      return response.data;
    } catch (requestError: any) {
      if (!silent) {
        enqueueSnackbar(requestError?.response?.data?.message || 'Failed to load orphan deletion history', { variant: 'error' });
      }
      return null;
    }
  }, [organizationId]);

  const fetchAuditSummary = useCallback(async () => {
    const requestId = ++summaryRequestRef.current;
    try {
      const response = await getS3StorageAuditSummary(organizationId);
      if (requestId !== summaryRequestRef.current || organizationIdRef.current !== organizationId) return null;
      setError('');
      setAudit((current) => (current ? { ...current, summary: response.data } : current));
      return response.data;
    } catch (requestError: any) {
      if (requestId !== summaryRequestRef.current || organizationIdRef.current !== organizationId) return null;
      setError(requestError?.response?.data?.message || 'Failed to load S3 storage status.');
      return null;
    }
  }, [organizationId]);

  useEffect(() => {
    getOrganizationRequest(organizationId)
      .then((response) => setOrganization(response.data))
      .catch(() => setOrganization(null));
  }, [organizationId]);

  useEffect(() => {
    setExpandedAssetId(null);
    setExpandedVideofeedId(null);
    fetchAudit();
  }, [fetchAudit]);

  useEffect(() => {
    summaryRequestRef.current += 1;
    deletionRequestRef.current += 1;
    setOrphanDeletions([]);
    setOrphansOnly(false);
    setPage(0);
  }, [organizationId]);

  useEffect(() => {
    void loadOrphanDeletions();
  }, [loadOrphanDeletions]);

  const scanStatus = audit?.summary?.status?.toUpperCase() || 'NOT_SCANNED';
  const scanInProgress = ACTIVE_SCAN_STATUSES.includes(scanStatus);
  const summary = audit?.summary;
  const orphanScan = summary?.orphan_scan;
  const orphanScanStatus = orphanScan?.status?.toUpperCase() || 'NOT_SCANNED';
  const orphanScanInProgress = ACTIVE_SCAN_STATUSES.includes(orphanScanStatus);
  const displayedBytes = summary?.displayed_storage_bytes ?? Number(organization?.storage_usage || 0) * 1_000_000_000;
  const hasCompletedScan = Boolean(summary?.last_scanned_at);
  const orphanFiles = orphansOnly ? audit?.documents || [] : [];
  const verifiedOrphanCount = orphanScan?.summary?.safe_candidates.count || 0;
  const verifiedOrphanBytes = orphanScan?.summary?.safe_candidates.size_bytes || 0;
  const referencedOrphanCount = orphanScan?.summary?.referenced.count || 0;
  const reviewOrphanCount = orphanScan?.summary?.needs_review.count || 0;
  const sharedScan = orphanScan?.shared_reference_scan;
  const missingSharedServers =
    sharedScan?.servers.filter((server) => !server.scanned).map((server) => server.server_prefix) || [];
  const hasCurrentOrphanPolicy = Number(orphanScan?.policy_version || 0) >= ORPHAN_SCAN_POLICY_VERSION;
  const hasOrphanResults = Boolean(orphanScan?.completed_at && orphanScan?.summary && hasCurrentOrphanPolicy);
  const activeOrphanDeletion = orphanDeletions.find(orphanDeletionIsActive);
  const activeOrphanDeletionId = activeOrphanDeletion?.id;
  const displayedOrphanDeletions = orphansOnly ? orphanDeletions : activeOrphanDeletion ? [activeOrphanDeletion] : [];
  const deletionBusy = deletionLoading || Boolean(activeOrphanDeletion);

  useEffect(() => {
    if (!scanInProgress && !orphanScanInProgress) return;
    let cancelled = false;
    let timeout: number | undefined;

    const poll = async () => {
      const latest = await fetchAuditSummary();
      if (cancelled) return;
      if (!latest) {
        await fetchAudit(true);
        if (!cancelled) timeout = window.setTimeout(poll, 5000);
        return;
      }
      const s3Active = ACTIVE_SCAN_STATUSES.includes(latest?.status?.toUpperCase() || '');
      const orphanActive = ACTIVE_SCAN_STATUSES.includes(latest?.orphan_scan?.status?.toUpperCase() || '');
      if (!s3Active && !orphanActive) {
        await fetchAudit();
        return;
      }
      timeout = window.setTimeout(poll, 5000);
    };

    timeout = window.setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      if (timeout) window.clearTimeout(timeout);
    };
  }, [scanInProgress, orphanScanInProgress, fetchAudit, fetchAuditSummary]);

  useEffect(() => {
    if (!activeOrphanDeletionId) return;
    let cancelled = false;
    let timeout: number | undefined;

    const poll = async () => {
      const deletions = await loadOrphanDeletions(true);
      if (cancelled) return;
      if (deletions && !deletions.some(orphanDeletionIsActive)) {
        await fetchAudit(true);
        return;
      }
      timeout = window.setTimeout(poll, 5000);
    };

    timeout = window.setTimeout(poll, 5000);
    return () => {
      cancelled = true;
      if (timeout) window.clearTimeout(timeout);
    };
  }, [activeOrphanDeletionId, fetchAudit, loadOrphanDeletions]);

  useEffect(() => {
    if (!socket) return;

    const onOrphanDeletion = (rawMessage: unknown) => {
      try {
        const message: any = typeof rawMessage === 'string' ? JSON.parse(rawMessage) : rawMessage;
        const payload: any = typeof message?.data === 'string' ? JSON.parse(message.data) : message?.data || message;
        if (Number(payload?.organization_id) !== Number(organizationId)) return;

        const operationId = Number(payload?.operation_id ?? payload?.id);
        const status = String(payload?.status || '').toUpperCase() as OrphanDeletion['status'];
        if (Number.isSafeInteger(operationId) && operationId > 0) {
          setOrphanDeletions((current) =>
            current.map((deletion) =>
              deletion.id === operationId
                ? {
                    ...deletion,
                    ...payload,
                    id: deletion.id,
                    organization_id: deletion.organization_id,
                    status: status || deletion.status,
                    progress_message: payload?.progress_message ?? message?.message ?? deletion.progress_message,
                  }
                : deletion,
            ),
          );
        }

        const event = String(message?.event || '').toLowerCase();
        if (['queued', 'started', 'processing'].includes(event)) {
          void loadOrphanDeletions(true);
        }
        if (['ACTIVE', 'PARTIAL', 'FAILED'].includes(status) || ['completed', 'failed'].includes(event)) {
          void loadOrphanDeletions(true);
          void fetchAudit(true);
        }
      } catch {
        // Ignore malformed progress events; durable polling remains the fallback.
      }
    };

    socket.on('orphan_deletion', onOrphanDeletion);
    return () => {
      socket.off('orphan_deletion', onOrphanDeletion);
    };
  }, [fetchAudit, loadOrphanDeletions, organizationId, socket]);

  const startScan = async () => {
    setScanLoading(true);
    try {
      const response = await startS3StorageAudit(organizationId);
      setAudit((current) =>
        current
          ? {
              ...current,
              summary: {
                ...current.summary,
                status: response.data.status,
                scan_requested_at: response.data.scan_requested_at,
                scan_error: null,
                orphan_scan: current.summary.orphan_scan
                  ? { ...current.summary.orphan_scan, stale: true }
                  : current.summary.orphan_scan,
              },
            }
          : current,
      );
      enqueueSnackbar('S3 storage scan started', { variant: 'success' });
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.response?.data?.message || 'Failed to start S3 storage scan', { variant: 'error' });
    } finally {
      setScanLoading(false);
    }
  };

  const startOrphanScan = async () => {
    if (orphansOnly) setPage(0);
    setOrphanScanLoading(true);
    try {
      const response = await startOrphanFileScan(organizationId);
      setAudit((current) =>
        current
          ? {
              ...current,
              summary: {
                ...current.summary,
                status: response.data.s3_status,
                scan_requested_at: response.data.s3_requested_at,
                scan_error: null,
                orphan_scan: {
                  status: response.data.status,
                  requested_at: response.data.requested_at,
                  started_at: null,
                  completed_at: current.summary.orphan_scan?.completed_at || null,
                  source_scan_at: current.summary.orphan_scan?.source_scan_at || null,
                  error: null,
                  stale: true,
                  summary: current.summary.orphan_scan?.summary || null,
                  shared_reference_scan: current.summary.orphan_scan?.shared_reference_scan,
                },
              },
            }
          : current,
      );
      enqueueSnackbar(
        response.data.s3_reused
          ? 'Using fresh S3 data; orphan verification started'
          : 'Refreshing S3 data, then verifying orphan files',
        { variant: 'success' },
      );
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.response?.data?.message || 'Failed to start orphan file scan', { variant: 'error' });
    } finally {
      setOrphanScanLoading(false);
    }
  };

  const deleteVerifiedOrphans = async () => {
    const completedAt = orphanScan?.completed_at;
    if (!hasCurrentOrphanPolicy || !verifiedOrphanCount || !sharedScan?.deletion_safe || !completedAt || orphanScan?.stale) return;
    const confirmed = window.confirm(
      `Create delete markers for ${verifiedOrphanCount.toLocaleString()} orphan file(s) (${formatStorageBytes(
        verifiedOrphanBytes,
      )})? They can be restored for 30 days.`,
    );
    if (!confirmed) return;

    setDeletionLoading(true);
    try {
      const response = await deleteOrphanFiles(organizationId, completedAt);
      setOrphanDeletions((current) => [response.data, ...current.filter((deletion) => deletion.id !== response.data.id)]);
      enqueueSnackbar(`Deletion queued for ${response.data.asset_count.toLocaleString()} orphan file(s).`, { variant: 'success' });
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.response?.data?.message || 'Failed to delete orphan files', { variant: 'error' });
      await loadOrphanDeletions();
    } finally {
      setDeletionLoading(false);
    }
  };

  const restoreDeletedOrphans = async (deletion: OrphanDeletion) => {
    if (!window.confirm(`Restore ${deletion.asset_count.toLocaleString()} orphan file(s) from this deletion?`)) return;

    setRestoreLoadingId(deletion.id);
    try {
      await restoreOrphanDeletion(organizationId, deletion.id);
      enqueueSnackbar('Orphan restore queued', { variant: 'success' });
      await loadOrphanDeletions();
      await fetchAudit(true);
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.response?.data?.message || 'Failed to restore orphan files', { variant: 'error' });
      await loadOrphanDeletions();
    } finally {
      setRestoreLoadingId(null);
    }
  };

  const cancelDeletingOrphans = async (deletion: OrphanDeletion) => {
    if (!window.confirm('Cancel this deletion and restore any files already hidden?')) {
      return;
    }
    setCancelLoadingId(deletion.id);
    try {
      await cancelOrphanDeletion(organizationId, deletion.id);
      enqueueSnackbar(
        'Cancellation requested. Created delete markers will be rolled back.',
        { variant: 'success' },
      );
      await loadOrphanDeletions();
    } catch (requestError: any) {
      enqueueSnackbar(
        requestError?.response?.data?.message || 'Failed to cancel orphan deletion',
        { variant: 'error' },
      );
      await loadOrphanDeletions();
    } finally {
      setCancelLoadingId(null);
    }
  };

  const exportCsv = async () => {
    setExportLoading(true);
    try {
      const filters = { ...requestFilters } as Partial<S3StorageAuditFilters>;
      delete filters.page;
      delete filters.limit;
      delete filters.groupBy;
      const blob = await exportS3StorageAudit(organizationId, filters as Omit<S3StorageAuditFilters, 'page' | 'limit'>);
      saveAs(blob, `${(organization?.name || `organisation-${organizationId}`).replace(/[^a-z0-9-_]+/gi, '-')}-s3-storage.csv`);
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.message || 'Failed to export S3 storage data', { variant: 'error' });
    } finally {
      setExportLoading(false);
    }
  };

  const toggleAsset = async (asset: S3StorageAuditAsset) => {
    if (expandedAssetId === asset.id) {
      setExpandedAssetId(null);
      return;
    }
    setExpandedAssetId(asset.id);
    if (assetDetails[asset.id]) return;

    setAssetLoadingId(asset.id);
    try {
      const response = await getS3StorageAuditAsset(organizationId, asset.id);
      setAssetDetails((current) => ({ ...current, [asset.id]: response.data }));
    } catch (requestError: any) {
      enqueueSnackbar(requestError?.response?.data?.message || 'Failed to load asset files', { variant: 'error' });
    } finally {
      setAssetLoadingId(null);
    }
  };

  const toggleVideofeed = (group: S3StorageAuditVideofeedGroup) => {
    const id = String(group.videofeed_id || 'none');
    setExpandedVideofeedId((current) => (current === id ? null : id));
  };

  const changeViewMode = (nextMode: 'videofeeds' | 'videos') => {
    if (nextMode === viewMode) return;
    setViewMode(nextMode);
    setPage(0);
  };

  const changeOrphanFilter = (checked: boolean) => {
    setLoading(true);
    setAudit((current) => (current ? { ...current, documents: [], videofeeds: [], total: 0 } : current));
    setOrphansOnly(checked);
    setPage(0);
  };

  const copyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      enqueueSnackbar('S3 key copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy S3 key', { variant: 'error' });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setClassification('');
    setFileType('');
    setDbStatus('');
    setDateFrom('');
    setDateTo('');
    setMinSizeGb('');
    setSort('size_desc');
    if (orphansOnly) changeOrphanFilter(false);
    else setPage(0);
  };

  const changeFilter =
    (setter: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      setter(event.target.value);
      setPage(0);
    };

  return (
    <div className={`${styles.page} CustomScrollbar`}>
      <header className={styles.header}>
        <div>
          <button type="button" className={styles.backButton} onClick={() => history.push(PATHS.ADMIN_PAGE.path)}>
            <ArrowBackIcon /> Back to organisations
          </button>
          <h1>{organization?.name || 'Organisation'} · S3 Storage</h1>
          <p>Comparison of displayed storage with current and verified legacy S3 paths, with guarded orphan cleanup.</p>
        </div>
        <div className={styles.headerActions}>
          <OutlinedButton onClick={exportCsv} disabled={!hasCompletedScan || exportLoading || orphansOnly}>
            <FileDownloadOutlinedIcon className={exportLoading ? styles.spinning : undefined} />
            {exportLoading ? 'Exporting…' : 'Export CSV'}
          </OutlinedButton>
          <OutlinedButton
            onClick={startOrphanScan}
            disabled={
              orphanScanLoading ||
              orphanScanInProgress ||
              scanLoading ||
              scanInProgress ||
              deletionBusy ||
              restoreLoadingId !== null
            }
          >
            <SearchIcon />
            {orphanScanInProgress ? 'Scanning orphan files…' : 'Scan for orphan files'}
          </OutlinedButton>
          <ContainedButton
            onClick={startScan}
            disabled={
              scanLoading ||
              scanInProgress ||
              orphanScanLoading ||
              orphanScanInProgress ||
              deletionBusy ||
              restoreLoadingId !== null
            }
          >
            <RefreshIcon className={scanInProgress ? styles.spinning : undefined} />
            {hasCompletedScan ? 'Refresh S3 data' : 'Scan S3 data'}
          </ContainedButton>
        </div>
      </header>

      {(loading || scanInProgress || orphanScanInProgress) && <LinearProgress />}
      {error && (
        <div className={styles.errorBanner}>
          <span>{error}</span>
          <button type="button" onClick={() => void fetchAudit()}>
            Retry
          </button>
        </div>
      )}

      <section className={styles.scanMeta}>
        <span className={`${styles.scanStatus} ${styles[scanStatus.toLowerCase()] || ''}`}>S3: {formatStorageLabel(scanStatus)}</span>
        <span>Last S3 scan: {formatStorageDate(summary?.last_scanned_at)}</span>
        <span className={`${styles.scanStatus} ${styles[orphanScanStatus.toLowerCase()] || ''}`}>
          Orphans: {formatStorageLabel(orphanScanStatus)}
        </span>
        {orphanScan?.completed_at && <span>Last orphan scan: {formatStorageDate(orphanScan.completed_at)}</span>}
        {summary?.scan_error && <span className={styles.scanError}>{summary.scan_error}</span>}
        {orphanScan?.error && <span className={styles.scanError}>{orphanScan.error}</span>}
      </section>

      <section className={styles.summaryGrid}>
        <SummaryCard label="Displayed storage usage" value={formatStorageBytes(displayedBytes)} />
        <SummaryCard
          label="Actual S3 (scanned paths)"
          value={formatStorageBytes(summary?.actual_storage_bytes)}
          detail={summary ? `${summary.object_count.toLocaleString()} objects · ${summary.asset_count.toLocaleString()} assets` : undefined}
        />
        <SummaryCard
          label="S3 minus displayed"
          value={formatStorageBytes(summary?.difference_bytes)}
          detail={
            summary?.difference_percent !== null && summary?.difference_percent !== undefined
              ? `${summary.difference_percent.toFixed(1)}%`
              : undefined
          }
        />
        <SummaryCard
          label={orphansOnly ? 'Deletable orphans' : 'Suspected orphans'}
          value={
            orphansOnly
              ? hasOrphanResults
                ? formatStorageBytes(verifiedOrphanBytes)
                : '—'
              : summary
              ? formatStorageBytes(summary.suspected_orphans_bytes)
              : '—'
          }
          detail={
            orphansOnly
              ? hasOrphanResults
                ? `${verifiedOrphanCount.toLocaleString()} deletable · ${(
                    summary?.suspected_orphans_count || 0
                  ).toLocaleString()} suspected`
                : undefined
              : summary
              ? `${summary.suspected_orphans_count.toLocaleString()} assets`
              : undefined
          }
        />
        <SummaryCard
          label="Archived but still present"
          value={summary ? formatStorageBytes(summary.archived_but_present_bytes) : '—'}
          detail={summary ? `${summary.archived_but_present_count.toLocaleString()} assets` : undefined}
        />
      </section>

      {displayedOrphanDeletions.length > 0 && (
        <section className={styles.reconciliationPanel}>
          <div className={styles.reconciliationHeader}>
            <div>
              <h2>Orphan file history</h2>
              <p>
                Delete markers hide files without removing their underlying S3 versions. Restoring removes only the exact markers created by
                that deletion.
              </p>
            </div>
          </div>
          <div className={styles.deletionHistoryList}>
            {displayedOrphanDeletions.map((deletion) => {
              const progress = orphanDeletionProgress(deletion);
              const inProgress = orphanDeletionIsActive(deletion);
              const cancelled =
                deletion.status === 'FAILED' &&
                deletion.error === 'Cancelled by administrator';
              const restoring = deletion.operation === 'RESTORE';
              const statusLabel =
                deletion.status === 'PENDING'
                  ? restoring
                    ? 'Restore queued'
                    : 'Queued'
                : deletion.status === 'PROCESSING' &&
                  !(Number(deletion.object_count || 0) > 0 && Number(deletion.processed_object_count || 0) >= Number(deletion.object_count || 0))
                ? `${restoring ? 'Restoring' : 'Deleting'} ${progress.completed.toLocaleString()} of ${deletion.object_count.toLocaleString()}`
                : deletion.status === 'RESTORED'
                ? 'Restored'
                : deletion.status === 'PROCESSING'
                ? 'Deleted'
                : deletion.status === 'PARTIAL'
                ? 'Needs attention'
                  : deletion.status === 'FAILED'
                  ? cancelled
                    ? 'Cancelled'
                    : 'Failed'
                  : 'Deleted';
              const statusDetail =
                deletion.status === 'PENDING'
                  ? deletion.progress_message || 'Waiting for the background worker'
                  : deletion.status === 'PROCESSING'
                  ? `${progress.percent.toFixed(0)}%${deletion.progress_message ? ` · ${deletion.progress_message}` : ''}`
                  : deletion.status === 'FAILED'
                  ? cancelled
                    ? 'Deletion cancelled; all created markers were rolled back'
                    : deletion.error || deletion.progress_message || 'Deletion failed'
                  : deletion.status === 'PARTIAL'
                  ? deletion.error || deletion.progress_message || `Restore until ${formatStorageDate(deletion.restore_until)}`
                  : deletion.restored_at
                  ? formatStorageDate(deletion.restored_at)
                  : deletion.status === 'ACTIVE' && deletion.completed_at
                  ? `Completed ${formatStorageDate(deletion.completed_at)} · Restore until ${formatStorageDate(deletion.restore_until)}`
                  : `Restore until ${formatStorageDate(deletion.restore_until)}`;

              return (
                <div className={styles.deletionHistoryRow} key={deletion.id}>
                  <span>
                    <strong>
                      {deletion.asset_count.toLocaleString()} files · {formatStorageBytes(deletion.total_size_bytes)}
                    </strong>
                    <small>
                      {deletion.object_count.toLocaleString()} objects · Requested {formatStorageDate(deletion.deleted_at)}
                    </small>
                  </span>
                  <span>
                    <strong>{statusLabel}</strong>
                    <small>{statusDetail}</small>
                    {inProgress && (
                      <LinearProgress
                        aria-label={`Orphan ${restoring ? 'restore' : 'deletion'} ${progress.percent.toFixed(0)}% complete`}
                        variant="determinate"
                        value={progress.percent}
                      />
                    )}
                  </span>
                  {inProgress && !restoring ? (
                    <button
                      type="button"
                      className={styles.restoreButton}
                      disabled={cancelLoadingId !== null}
                      onClick={() => cancelDeletingOrphans(deletion)}
                    >
                      <CancelOutlinedIcon /> {cancelLoadingId === deletion.id ? 'Cancelling…' : 'Cancel deletion'}
                    </button>
                  ) : !inProgress && deletion.can_restore ? (
                    <button
                      type="button"
                      className={styles.restoreButton}
                      disabled={
                        restoreLoadingId !== null ||
                        scanLoading ||
                        scanInProgress ||
                        orphanScanLoading ||
                        orphanScanInProgress ||
                        deletionBusy
                      }
                      onClick={() => restoreDeletedOrphans(deletion)}
                    >
                      <RestoreOutlinedIcon /> {restoreLoadingId === deletion.id ? 'Restoring…' : 'Restore'}
                    </button>
                  ) : (
                    <span className={styles.restoreUnavailable}>
                      {deletion.status === 'RESTORED'
                        ? 'Completed'
                        : inProgress
                        ? 'In progress'
                        : deletion.status === 'FAILED'
                        ? 'Failed'
                        : 'Restore unavailable'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {!hasCompletedScan && !loading ? (
        <section className={styles.notScanned}>
          <h2>S3 data has not been scanned yet</h2>
          <p>Start a read-only scan to compare current S3 objects with database records for this organisation.</p>
          <ContainedButton onClick={startScan} disabled={scanLoading || scanInProgress}>
            Scan S3 data
          </ContainedButton>
        </section>
      ) : (
        <>
          {!orphansOnly && (
            <section className={styles.breakdownGrid}>
              <Breakdown title="Files by type" items={audit?.breakdown.file_types || []} />
              <Breakdown title="Assets by state" items={audit?.breakdown.classifications || []} />
              <Breakdown title="Why S3 differs" items={audit?.breakdown.disparity || []} />
            </section>
          )}

          <section className={styles.assetsSection}>
            <div className={styles.sectionHeading}>
              <div>
                <h2>{orphansOnly ? 'Orphan files' : viewMode === 'videofeeds' ? 'Videofeeds' : 'Videos'}</h2>
                <p>
                  {orphansOnly
                    ? 'This is the last completed orphan scan. Delete removes every listed orphan across all pages.'
                    : viewMode === 'videofeeds'
                    ? 'One row groups the unique physical video assets referenced by a videofeed.'
                    : 'One row represents one physical video asset prefix.'}
                </p>
              </div>
              <div className={styles.sectionHeadingActions}>
                <span>
                  {orphansOnly
                    ? `${(audit?.total || 0).toLocaleString()} files`
                    : `${(audit?.total || 0).toLocaleString()} ${viewMode === 'videofeeds' ? 'groups' : 'videos'}`}
                </span>
                {!orphansOnly && (
                  <div className={styles.viewToggle}>
                    <button
                      type="button"
                      className={viewMode === 'videofeeds' ? styles.activeView : undefined}
                      onClick={() => changeViewMode('videofeeds')}
                    >
                      Videofeeds
                    </button>
                    <button
                      type="button"
                      className={viewMode === 'videos' ? styles.activeView : undefined}
                      onClick={() => changeViewMode('videos')}
                    >
                      Videos
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.filters}>
              {!orphansOnly && (
                <>
                  <input
                    aria-label={viewMode === 'videofeeds' ? 'Search videofeeds' : 'Search S3 videos'}
                    placeholder={
                      viewMode === 'videofeeds' ? 'Search videofeed, video or S3 key' : 'Search S3 key, video or videofeed ID'
                    }
                    value={search}
                    onChange={changeFilter(setSearch)}
                  />
                  <select aria-label="Classification" value={classification} onChange={changeFilter(setClassification)}>
                    <option value="">All classifications</option>
                    {CLASSIFICATIONS.map((value) => (
                      <option key={value} value={value}>
                        {formatStorageLabel(value)}
                      </option>
                    ))}
                  </select>
                  <select aria-label="File type" value={fileType} onChange={changeFilter(setFileType)}>
                    <option value="">All file types</option>
                    {FILE_TYPES.map((value) => (
                      <option key={value} value={value}>
                        {formatStorageLabel(value)}
                      </option>
                    ))}
                  </select>
                  <select aria-label="Database status" value={dbStatus} onChange={changeFilter(setDbStatus)}>
                    <option value="">All DB statuses</option>
                    {DB_STATUSES.map((value) => (
                      <option key={value} value={value}>
                        {formatStorageLabel(value)}
                      </option>
                    ))}
                  </select>
                  <input aria-label="From date" type="date" value={dateFrom} onChange={changeFilter(setDateFrom)} />
                  <input aria-label="To date" type="date" value={dateTo} onChange={changeFilter(setDateTo)} />
                  <input
                    aria-label="Minimum size in GB"
                    min="0"
                    placeholder="Min size (GB)"
                    step="0.1"
                    type="number"
                    value={minSizeGb}
                    onChange={changeFilter(setMinSizeGb)}
                  />
                  <select
                    aria-label={viewMode === 'videofeeds' ? 'Sort videofeeds' : 'Sort videos'}
                    value={sort}
                    onChange={changeFilter(setSort)}
                  >
                    <option value="size_desc">Largest first</option>
                    <option value="size_asc">Smallest first</option>
                    <option value="modified_desc">Newest first</option>
                    <option value="modified_asc">Oldest first</option>
                  </select>
                </>
              )}
              <label className={styles.checkboxFilter}>
                <input
                  checked={orphansOnly}
                  disabled={deletionBusy || restoreLoadingId !== null}
                  type="checkbox"
                  onChange={(event) => changeOrphanFilter(event.target.checked)}
                />
                Orphans only
              </label>
              {orphansOnly && (
                <button
                  type="button"
                  className={styles.deleteOrphansButton}
                  disabled={
                    !hasCurrentOrphanPolicy ||
                    !verifiedOrphanCount ||
                    !sharedScan?.deletion_safe ||
                    orphanScanStatus !== 'COMPLETED' ||
                    !orphanScan?.completed_at ||
                    orphanScan?.stale ||
                    orphanScanLoading ||
                    orphanScanInProgress ||
                    loading ||
                    !!error ||
                    deletionBusy ||
                    scanInProgress ||
                    restoreLoadingId !== null
                  }
                  title={
                    activeOrphanDeletion
                      ? activeOrphanDeletion.status === 'PENDING'
                        ? 'Orphan deletion is queued'
                        : 'Orphan deletion is in progress'
                      : !hasCurrentOrphanPolicy || orphanScanStatus !== 'COMPLETED' || !orphanScan?.completed_at
                      ? 'Run Scan for orphan files first'
                      : orphanScan?.stale
                      ? 'S3 data changed after this result. Scan for orphan files again.'
                      : !verifiedOrphanCount
                      ? 'No orphan files found'
                      : sharedScan?.deletion_safe
                      ? 'Create recoverable S3 delete markers'
                      : 'Scan every registered server using this bucket before deleting orphan files'
                  }
                  onClick={deleteVerifiedOrphans}
                >
                  <DeleteOutlineIcon />
                  {deletionLoading
                    ? 'Queuing…'
                    : activeOrphanDeletion?.status === 'PENDING'
                    ? 'Deletion queued…'
                    : activeOrphanDeletion
                    ? `Deleting ${orphanDeletionProgress(activeOrphanDeletion).completed.toLocaleString()} of ${activeOrphanDeletion.object_count.toLocaleString()}…`
                    : 'Delete orphan files'}
                </button>
              )}
              <button
                type="button"
                className={styles.clearFilters}
                disabled={deletionBusy || restoreLoadingId !== null}
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>

            {orphansOnly ? (
              hasOrphanResults ? (
                <>
                  <div className={styles.orphanFilesSummary}>
                    <strong>{verifiedOrphanCount.toLocaleString()} orphan files</strong>
                    <span>{formatStorageBytes(verifiedOrphanBytes)}</span>
                    <small>
                      S3 scan {formatStorageDate(orphanScan?.source_scan_at)} · Shared scan {sharedScan?.servers_scanned || 0}/
                      {sharedScan?.servers_total || 0} servers
                      {orphanScan?.stale ? ' · New orphan scan required' : ''}
                      {orphanScanInProgress ? ' · New scan in progress' : ''}
                      {referencedOrphanCount > 0
                        ? ` · ${referencedOrphanCount.toLocaleString()} referenced protected`
                        : ''}
                      {reviewOrphanCount > 0
                        ? ` · ${reviewOrphanCount.toLocaleString()} require refresh or review`
                        : ''}
                      {sharedScan?.complete && !sharedScan.deletion_safe ? ' · Deletion unavailable for this scan' : ''}
                    </small>
                  </div>
                  {loading ? (
                    <div className={styles.noOrphanFiles}>Loading saved orphan files…</div>
                  ) : orphanFiles.length ? (
                    <div className={`${styles.reconciliationList} CustomScrollbar`}>
                      {orphanFiles.map((asset) => (
                        <div className={styles.orphanFileRow} key={asset.id}>
                          <span className={styles.reconciliationAsset}>
                            <strong>{asset.name || asset.s3_prefix}</strong>
                            <small title={asset.s3_prefix}>{asset.s3_prefix}</small>
                          </span>
                          <span>{formatStorageBytes(asset.total_size_bytes)}</span>
                          <span>{asset.object_count.toLocaleString()} objects</span>
                          <LastModified value={asset.last_modified_at} />
                          <OrphanFileBadge />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noOrphanFiles}>
                      {reviewOrphanCount > 0 && sharedScan?.configured === false
                        ? `${reviewOrphanCount.toLocaleString()} suspected orphan file(s) cannot be deleted from this local or unregistered backend.`
                        : reviewOrphanCount > 0 && !sharedScan?.complete
                        ? `${reviewOrphanCount.toLocaleString()} suspected orphan file(s) are waiting for shared-server verification. Refresh Server storage on ${
                            missingSharedServers.join(', ') || 'every missing server'
                          }, then run Scan for orphan files again.`
                        : reviewOrphanCount > 0 && sharedScan?.complete && !sharedScan.deletion_safe
                        ? `${reviewOrphanCount.toLocaleString()} suspected orphan file(s) are waiting for a matching fresh Shared scan. Refresh Server storage on every registered server, then scan again.`
                        : reviewOrphanCount > 0
                        ? `${reviewOrphanCount.toLocaleString()} suspected orphan file(s) could not be safely verified. Refresh the required server data and scan again.`
                        : referencedOrphanCount > 0
                        ? `No deletable orphan files. ${referencedOrphanCount.toLocaleString()} suspected file(s) still have database references and remain protected.`
                        : sharedScan?.deletion_safe
                        ? 'No orphan files found.'
                        : sharedScan?.complete
                        ? 'No orphan files found in the completed shared scan.'
                        : 'Scan every registered server using this bucket before orphan files can be confirmed.'}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.noOrphanFiles}>
                  {orphanScanInProgress || orphanScanLoading
                    ? 'Scanning for orphan files…'
                    : !hasCurrentOrphanPolicy && orphanScan?.completed_at
                    ? 'The orphan rules were updated. Press Scan for orphan files to refresh this result.'
                    : orphanScanStatus === 'FAILED'
                    ? orphanScan?.error || 'The orphan scan could not be completed. Press Scan for orphan files to try again.'
                    : 'No orphan scan has been completed yet. Press Scan for orphan files.'}
                </div>
              )
            ) : viewMode === 'videofeeds' ? (
              <div className={styles.tableScroll}>
                <div className={`${styles.table} ${styles.videofeedTable}`}>
                  <div className={`${styles.tableHeader} ${styles.videofeedHeader}`}>
                    <span />
                    <span>Videofeed</span>
                    <span>Total size</span>
                    <span>Source / HLS</span>
                    <span>Videos</span>
                    <span>Assets</span>
                    <span>Status</span>
                    <span>Last modified</span>
                  </div>

                  {!loading && !audit?.videofeeds?.length && <div className={styles.emptyTable}>No videofeeds match these filters.</div>}
                  {audit?.videofeeds?.map((group) => {
                    const groupId = String(group.videofeed_id || 'none');
                    const expanded = expandedVideofeedId === groupId;
                    const groupTypeLabel =
                      group.is_reference_timeline === true
                        ? 'Reference Timeline'
                        : group.type === 'MY_COMPOSER'
                          ? 'Online Composer'
                          : null;
                    return (
                      <React.Fragment key={groupId}>
                        <button
                          type="button"
                          className={`${styles.assetRow} ${styles.videofeedRow}`}
                          onClick={() => toggleVideofeed(group)}
                        >
                          <span className={styles.expandIcon}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</span>
                          <span className={styles.assetName}>
                            <strong>{group.title}</strong>
                            <small>
                              {group.videofeed_id
                                ? `ID ${group.videofeed_id}${groupTypeLabel ? ` · ${groupTypeLabel}` : ''}`
                                : 'Assets without a videofeed reference'}
                            </small>
                          </span>
                          <span>{formatStorageBytes(group.total_size_bytes)}</span>
                          <span className={styles.sizeSplit}>
                            <small>{formatStorageBytes(group.source_size_bytes)} source</small>
                            <small>{formatStorageBytes(group.hls_size_bytes)} HLS</small>
                          </span>
                          <span>{group.video_ids.length.toLocaleString()}</span>
                          <Tooltip
                            arrow
                            placement="top"
                            title="Physical is the total number of S3 video assets in this videofeed. Shared is the subset also referenced by another videofeed; it is included in Physical, not added on top."
                          >
                            <span className={styles.dbCell}>
                              <small>{group.asset_count.toLocaleString()} physical</small>
                              {group.shared_asset_count > 0 && <small>{group.shared_asset_count.toLocaleString()} shared</small>}
                            </span>
                          </Tooltip>
                          <span className={styles.dbCell}>
                            <small>{group.status ? formatStorageLabel(group.status) : 'No videofeed row'}</small>
                            <small>{group.classifications.map(formatStorageLabel).join(', ')}</small>
                          </span>
                          <LastModified value={group.last_modified_at} />
                        </button>

                        {expanded && (
                          <div className={styles.videofeedDetails}>
                            <div className={styles.assetMeta}>
                              <div>
                                <span>Physical storage</span>
                                <strong>{formatStorageBytes(group.total_size_bytes)}</strong>
                              </div>
                              <div>
                                <span>Video IDs</span>
                                <strong>{group.video_ids.join(', ') || '—'}</strong>
                              </div>
                              <div>
                                <span>Thumbnails / metadata / other</span>
                                <strong>
                                  {formatStorageBytes(group.thumbnail_size_bytes)} / {formatStorageBytes(group.metadata_size_bytes)} /{' '}
                                  {formatStorageBytes(group.other_size_bytes)}
                                </strong>
                              </div>
                            </div>
                            <div className={styles.feedAssetsHeader}>
                              <span>Video / S3 prefix</span>
                              <span>Total</span>
                              <span>Source / HLS</span>
                              <span>Database</span>
                              <span>Classification</span>
                              <span>Last modified</span>
                            </div>
                            {group.assets.map((asset) => (
                              <div className={styles.feedAssetRow} key={asset.id}>
                                <span className={styles.assetName}>
                                  <strong>{asset.name || asset.s3_prefix}</strong>
                                  <small title={asset.s3_prefix}>{asset.s3_prefix}</small>
                                </span>
                                <span>{formatStorageBytes(asset.total_size_bytes)}</span>
                                <span className={styles.sizeSplit}>
                                  <small>{formatStorageBytes(asset.source_size_bytes)} source</small>
                                  <small>{formatStorageBytes(asset.hls_size_bytes)} HLS</small>
                                </span>
                                <span className={styles.dbCell}>
                                  <small>{asset.video_ids.length ? `Video ${asset.video_ids.join(', ')}` : 'No video row'}</small>
                                  <small>{asset.db_statuses.map(formatStorageLabel).join(', ') || 'No DB status'}</small>
                                </span>
                                <span className={styles.classificationCell}>
                                  <span className={`${styles.classification} ${styles[asset.classification.toLowerCase()] || ''}`}>
                                    {formatStorageLabel(asset.classification)}
                                  </span>
                                  <StorageScopeBadge scope={asset.storage_scope} />
                                </span>
                                <LastModified value={asset.last_modified_at} />
                              </div>
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className={styles.tableScroll}>
                <div className={styles.table}>
                  <div className={styles.tableHeader}>
                    <span />
                    <span>Asset / S3 prefix</span>
                    <span>Total size</span>
                    <span>Source / HLS</span>
                    <span>Objects</span>
                    <span>Database</span>
                    <span>Classification</span>
                    <span>References</span>
                    <span>Last modified</span>
                  </div>

                  {!loading && !audit?.documents.length && <div className={styles.emptyTable}>No assets match these filters.</div>}
                  {audit?.documents.map((asset) => {
                    const expanded = expandedAssetId === asset.id;
                    const details = assetDetails[asset.id];
                    return (
                      <React.Fragment key={asset.id}>
                        <button type="button" className={styles.assetRow} onClick={() => toggleAsset(asset)}>
                          <span className={styles.expandIcon}>{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}</span>
                          <span className={styles.assetName}>
                            <strong>{asset.name || asset.s3_prefix}</strong>
                            <small title={asset.s3_prefix}>{asset.s3_prefix}</small>
                          </span>
                          <span>{formatStorageBytes(asset.total_size_bytes)}</span>
                          <span className={styles.sizeSplit}>
                            <small>{formatStorageBytes(asset.source_size_bytes)} source</small>
                            <small>{formatStorageBytes(asset.hls_size_bytes)} HLS</small>
                          </span>
                          <span>{asset.object_count.toLocaleString()}</span>
                          <span className={styles.dbCell}>
                            <small>{asset.video_ids.length ? `${asset.video_ids.length} video row(s)` : 'No video row'}</small>
                            <small>{asset.db_statuses.length ? asset.db_statuses.join(', ') : 'No DB status'}</small>
                          </span>
                          <span className={styles.classificationCell}>
                            <span className={`${styles.classification} ${styles[asset.classification.toLowerCase()] || ''}`}>
                              {formatStorageLabel(asset.classification)}
                            </span>
                            <StorageScopeBadge scope={asset.storage_scope} />
                          </span>
                          <span>{asset.reference_count}</span>
                          <LastModified value={asset.last_modified_at} />
                        </button>

                        {expanded && (
                          <div className={styles.assetDetails}>
                            {assetLoadingId === asset.id ? (
                              <LinearProgress />
                            ) : details ? (
                              <>
                                <div className={styles.assetMeta}>
                                  <div>
                                    <span>Reason</span>
                                    <strong>{details.classification_reason || '—'}</strong>
                                  </div>
                                  <div>
                                    <span>Video IDs</span>
                                    <strong>{details.video_ids.join(', ') || '—'}</strong>
                                  </div>
                                  <div>
                                    <span>Videofeeds</span>
                                    <strong>
                                      {details.videofeeds.map((feed) => `${feed.title || 'Untitled'} (${feed.id})`).join(', ') || '—'}
                                    </strong>
                                  </div>
                                  <div>
                                    <span>Thumbnails / metadata / other</span>
                                    <strong>
                                      {formatStorageBytes(details.thumbnail_size_bytes)} / {formatStorageBytes(details.metadata_size_bytes)}{' '}
                                      / {formatStorageBytes(details.other_size_bytes)}
                                    </strong>
                                  </div>
                                  {details.uploaded_by && (
                                    <div>
                                      <span>Uploaded by</span>
                                      <strong>{details.uploaded_by}</strong>
                                    </div>
                                  )}
                                  {details.uploaded_at && (
                                    <div>
                                      <span>Uploaded</span>
                                      <strong>{formatStorageDate(details.uploaded_at)}</strong>
                                    </div>
                                  )}
                                </div>

                                {!!details.references?.length && (
                                  <div className={styles.references}>
                                    <h4>Database references</h4>
                                    {details.references.map((reference) => (
                                      <span key={`${reference.type}-${reference.id}`}>
                                        {formatStorageLabel(reference.type)} · {reference.title || reference.id}
                                        {reference.status ? ` · ${formatStorageLabel(reference.status)}` : ''}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                <div className={styles.objectsHeader}>
                                  <h4>S3 objects</h4>
                                  {details.objects.length > 200 && (
                                    <span>Showing first 200 of {details.objects.length.toLocaleString()}</span>
                                  )}
                                </div>
                                <div className={styles.objectsList}>
                                  {details.objects.slice(0, 200).map((object) => (
                                    <div className={styles.objectRow} key={object.key}>
                                      <span title={object.key}>{object.key}</span>
                                      <span>{formatStorageLabel(object.file_type)}</span>
                                      <span>{formatStorageBytes(object.size_bytes)}</span>
                                      <LastModified value={object.last_modified_at} />
                                      <button type="button" title="Copy S3 key" onClick={() => copyKey(object.key)}>
                                        <ContentCopyOutlinedIcon />
                                      </button>
                                    </div>
                                  ))}
                                  {!details.objects.length && <div className={styles.emptyObjects}>No current S3 objects.</div>}
                                </div>
                              </>
                            ) : (
                              <div className={styles.emptyObjects}>Asset details could not be loaded.</div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        </>
      )}

      {hasCompletedScan && (!orphansOnly || hasOrphanResults) && (
        <PaginationFooter
          hideVersion
          total={audit?.total || 0}
          page={page}
          rowsPerPage={limit}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setLimit(Number(event.target.value));
            setPage(0);
          }}
        />
      )}
    </div>
  );
};

export default S3StorageDetails;
